import {
  Button,
  Card,
  createTableColumn,
  Select,
  SelectProps,
  Table,
  TableBody,
  TableCell,
  TableColumnDefinition,
  TableColumnId,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
  useId,
  useTableFeatures,
  useTableSort
} from "@fluentui/react-components";
import { ArrowLeftRegular } from "@fluentui/react-icons"
import React from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { makeDocsLink } from "@tdp/common/constants";
import { Flex } from "@tdp/common/fluentV9MigrationComponents";
import { useSelector } from "@tdp/core";

import { usageByRegion } from "../abstractions/constants";
import { Area } from "../abstractions/IAnalyticsFilters";
import { AppPage } from "../AppPage";
import { AppPageHeader } from "../AppPageHeader";
import { ChartMapBubble, ICountryData, Period } from "../charts";
import { useFilterFormattedString } from "../filters";
import { analyticsV2MetricsSlice } from "../reducer";
import { useAnalyticsData } from "../useAnalyticsData";

const RegionDetails: React.FC = React.memo(() => {
  useAnalyticsData(usageByRegion)

  const selectId = useId();
  const intl = useIntl();
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = React.useState<Period>(Period.Monthly);
  const usageByRegionData = useSelector(analyticsV2MetricsSlice.selectors.getRegionWidgetData);
  const lastUpdated = useSelector((state) => state.analyticsWidgets.LastUpdated);
  const data = usageByRegionData[selectedPeriod];
  const regionDetailsFormattedStrings = useFilterFormattedString(intl);
  const headerTitle =
    intl.formatMessage({ defaultMessage: "Usage by country/region", description: "Title of the card the shows a chart that displays usage by region in Region Details page" });
  const headerDescription = intl.formatMessage({ defaultMessage: "Get an overview of your app's usage, engagement, and other insights.", description: "The description for the analytics page." });
  const onBack = () => { navigate(-1); };

  const onChange: SelectProps["onChange"] = (_, data) => {
    setSelectedPeriod(data.value as Period);
  };

  const activeMauShortName = intl.formatMessage({ defaultMessage: "Active users", description: "Short name for the metric analyticsMetricNameMau30. For metric explanation see analyticsMetricTooltipTextMau30" });

  const columns = [
    { key: "countries", value: intl.formatMessage({ defaultMessage: "Country/Region", description: "Country or region display text on column" }) },
    { key: "analyticsMetricShortNameActiveMau", value: activeMauShortName },
    { key: "percentage", value: `${activeMauShortName} (%)` }
  ];

  const columnsSort: TableColumnDefinition<ICountryData>[] = [
    createTableColumn<ICountryData>({
      columnId: "countries",
      compare: (a, b) => {
        return a.name.localeCompare(b.name);
      },
    }),
    createTableColumn<ICountryData>({
      columnId: "analyticsMetricShortNameActiveMau",
      compare: (a, b) => {
        return a.z - b.z;
      },
    }),
    createTableColumn<ICountryData>({
      columnId: "percentage",
      compare: (a, b) => {
        return a.z - b.z;
      },
    }),
  ];

  // ChevronStartIcon was used in the original code, but it is not imported in this file
  if (data === undefined) return (
    <Flex column fill>
      <Flex gap="gap.medium">
        <Button icon={<ArrowLeftRegular />} onClick={onBack} />
        <AppPageHeader
          hasSections title={headerTitle}
          description={headerDescription}
        />
      </Flex>
      <Flex fill vAlign="center" hAlign="center">
        {intl.formatMessage({ defaultMessage: "No data available", description: "Message displayed when there is no data available for analytics" })}
      </Flex>
    </Flex>
  )

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures(
    {
      columns: columnsSort,
      items: data,
    },
    [
      useTableSort({
        defaultSortState: { sortColumn: "analyticsMetricShortNameActiveMau", sortDirection: "descending" },
      }),
    ]
  );

  const headerSortProps = (columnId: TableColumnId) => ({
    onClick: (e: React.MouseEvent) => {
      toggleColumnSort(e, columnId);
    },
    sortDirection: getSortDirection(columnId),
  });

  const rows = sort(getRows());

  const TableData = () => {
    const totalUsage = data.reduce((acc, obj) => acc + obj.z, 0);
    const area = [
      { filterType: Area.AllUp, key: "analyticsFilterAllLabel" },
      { filterType: Area.UnitedStates, key: "analyticsFilterAreaUnitedStatesLabel" },
      { filterType: Area.WesternEurope, key: "analyticsFilterAreaWesternEuropeLabel" },
      { filterType: Area.LatinAmerica, key: "analyticsFilterAreaLatinAmericaLabel" },
      { filterType: Area.Japan, key: "analyticsFilterAreaJapanLabel" },
      { filterType: Area.India, key: "analyticsFilterAreaIndiaLabel" },
      { filterType: Area.Canada, key: "analyticsFilterAreaCanadaLabel" },
      { filterType: Area.AustraliaAndNewZealand, key: "analyticsFilterAreaAustraliaLabel" },
      { filterType: Area.UnitedKingdom, key: "analyticsFilterAreaUnitedKingdomLabel" },
      { filterType: Area.France, key: "analyticsFilterAreaFranceLabel" },
      { filterType: Area.AsiaPacific, key: "analyticsFilterAreaAsiaPacificLabel" },
      { filterType: Area.Germany, key: "analyticsFilterAreaGermanyLabel" },
      { filterType: Area.Switzerland, key: "analyticsFilterAreaSwitzerlandLabel" },
      { filterType: Area.Korea, key: "analyticsFilterAreaKoreaLabel" },
      { filterType: Area.Netherlands, key: "analyticsFilterAreaNetherlandsLabel" },
      { filterType: Area.CentralEuropeAndMiddleEastAndAfrica, key: "analyticsFilterAreaCentralAndEasternEuropeLabel" }
    ]
    return <TableBody>
      {rows.map((data) => {
        const filter = area.find(item => item.filterType === data.item.name);
        return (
          <TableRow>
            <TableCell>
              <Flex gap="gap.small" vAlign="center">
                {filter
                  ?
                  filter.key in regionDetailsFormattedStrings
                    ? regionDetailsFormattedStrings[filter.key as keyof typeof regionDetailsFormattedStrings]
                    : intl.formatMessage({ id: filter.key, defaultMessage: data.item.name })
                  : data.item.name}
              </Flex>
            </TableCell>
            <TableCell>
              <Flex>
                <Tooltip
                  content={
                    <div style={{ padding: "8px" }}>
                      {`${activeMauShortName}: ${data.item.z}`}
                    </div>
                  }
                  relationship="label">
                  <div>{((data.item.z / totalUsage) * 100).toFixed(2)}%</div>
                </Tooltip>
              </Flex>
            </TableCell>
          </TableRow>
        )
      })}
    </TableBody>
  };

  return <AppPage>
    <Flex gap="gap.medium">
      <Button icon={<ArrowLeftRegular />} onClick={onBack} />
      <AppPageHeader
        hasSections title={headerTitle}
        description={headerDescription}
        learnMoreLink={{
          href: makeDocsLink(
            "microsoftteams/platform/messaging-extensions/build-api-based-message-extension?tabs=api-service-auth#register-an-api-key"
          ),
          localisedCaption: intl.formatMessage({ defaultMessage: "Learn more", description: "Learn more link for analytics" })
        }}
      />
    </Flex>
    <Flex column gap="gap.medium" style={{ marginTop: "32px" }}>
      <Select
        style={{ width: "200px" }}
        id={selectId}
        onChange={onChange} value={selectedPeriod}>
        <option value={Period.Daily}>{intl.formatMessage({ defaultMessage: "Daily (DAU)", description: "Title for daily analytics widget", })}</option>
        <option value={Period.Weekly}>{intl.formatMessage({ defaultMessage: "Weekly (WAU)", description: "Title for weekly analytics widget" })}</option>
        <option value={Period.Monthly}>{intl.formatMessage({ defaultMessage: "Monthly (MAU)", description: "Title for monthly analytics widget" })}</option>
      </Select>
      <Card style={{ padding: "32px" }}>
        <AppPageHeader
          hasSections title={headerTitle}
          description={
            intl.formatMessage({
              defaultMessage: "Last updated: {dateTime}",
              description: "Last updated date time for analytics",
            }, { dateTime: intl.formatDate(lastUpdated.widgetData) })}
        />
        <Flex fill>
          <ChartMapBubble data={data} legend={intl.formatMessage({ defaultMessage: "App usage", description: "Tab title for app usage analytics" })} />
        </Flex>
        <Table arial-label={headerTitle} style={{ marginTop: "32px" }} sortable>
          <TableHeader>
            <TableRow>
              {[columns[0], columns[2]].map((column) => (
                <TableHeaderCell key={column.key} {...headerSortProps(column.key)}>
                  {column.value}
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableData />
        </Table>
      </Card>
    </Flex>
  </AppPage>
});

export default RegionDetails;
